import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  FormHelperText,
  Grid,
  Link,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { ArrowUpRight } from "@phosphor-icons/react";
import { ResponsiveModal } from "@answersnow/pkg-theme";
import { Link as RouterLink } from "react-router-dom";

import useGetCustomerPhysicians from "hooks/api/client/useGetCustomerPhysicians";
import useGetAllDocumentRequests from "hooks/api/client/useGetAllDocumentRequests";
import { addYears } from "date-fns";

const DOSAGE_UNITS = ["ml", "oz", "mg", "g", "mcg", "l", "cc", "unit"];
const FREQUENCY_INTERVALS = ["hour", "day", "week", "month", "quarter", "year"];

interface Physician {
  id: number;
  name: string | null;
  clinic_id: number | null;
}

interface DocumentRequest {
  id: number;
  provider_id: number | null;
  clinic_id: number | null;
  signature_url: string | null;
  document_id: string | null;
  created: string;
}

interface MedicationFormValues {
  name: string;
  dosageAmount: string;
  dosageUnit: string;
  purpose: string;
  frequencyAmount: string;
  frequencyInterval: string;
  prescribingDoctor: string;
  collaborationScheduleValue: string;
  collaborationScheduleInterval: string;
}

export interface MedicationModalSaveValues {
  name: string;
  dosageAmount: string;
  dosageUnit: string;
  purpose: string;
  frequencyAmount: string;
  frequencyInterval: string;
  prescribingDoctor: string;
  collaborationScheduleValue: string;
  collaborationScheduleInterval: string;
}

const INITIAL_FORM: MedicationFormValues = {
  name: "",
  dosageAmount: "",
  dosageUnit: "ml",
  purpose: "",
  frequencyAmount: "",
  frequencyInterval: "day",
  prescribingDoctor: "",
  collaborationScheduleValue: "",
  collaborationScheduleInterval: "month",
};

interface FormErrors {
  name?: string;
  dosageAmount?: string;
  frequencyAmount?: string;
}

function validate(values: MedicationFormValues): FormErrors {
  const errors: FormErrors = {};
  if (!values.name.trim()) errors.name = "Name is required";
  if (!values.dosageAmount) errors.dosageAmount = "Dosage amount is required";
  if (!values.frequencyAmount) errors.frequencyAmount = "Frequency amount is required";
  return errors;
}

interface MedicationsModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (values: MedicationModalSaveValues) => void;
  isSaving?: boolean;
  initialValues?: Partial<MedicationFormValues>;
  clientId: string | number;
}

export const MedicationsModal: React.FC<MedicationsModalProps> = ({
  open,
  onClose,
  onSave,
  isSaving = false,
  initialValues,
  clientId,
}) => {
  const { physicians } = useGetCustomerPhysicians(clientId);
  const { documentRequests } = useGetAllDocumentRequests(clientId);

  const getRoiLabel = (physician: Physician): string | null => {
    const signed = (documentRequests as DocumentRequest[] ?? []).filter(
      (req) =>
        (req.provider_id === physician.id || req.clinic_id === physician.clinic_id) &&
        (req.signature_url || req.document_id)
    );
    if (signed.length === 0) return null;
    const latest = [...signed].sort(
      (a, b) => new Date(b.created).getTime() - new Date(a.created).getTime()
    )[0];
    const expired = addYears(new Date(latest.created), 1) < new Date();
    return expired ? "ROI expired" : "ROI on file";
  };

  const [formValues, setFormValues] = useState<MedicationFormValues>({
    ...INITIAL_FORM,
    ...initialValues,
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [isCustomProvider, setIsCustomProvider] = useState(false);
  // Tracks the selected physician by ID so the Select value is unambiguous
  const [selectedPhysicianId, setSelectedPhysicianId] = useState<string>("");

  useEffect(() => {
    if (open) {
      setFormValues({ ...INITIAL_FORM, ...initialValues });
      setErrors({});
      const matchedPhysician = (physicians as Physician[] ?? []).find(
        (m) => m.name === initialValues?.prescribingDoctor
      );
      const isCustom = !!initialValues?.prescribingDoctor && !matchedPhysician;
      setIsCustomProvider(isCustom);
      setSelectedPhysicianId(matchedPhysician ? String(matchedPhysician.id) : "");
    }
  }, [open, initialValues, physicians]);

  const handleChange =
    (field: keyof MedicationFormValues) =>
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setFormValues((prev) => ({ ...prev, [field]: event.target.value }));
    };

  const handleSelectChange = (field: keyof MedicationFormValues) => (value: string) => {
    setFormValues((prev) => ({ ...prev, [field]: value }));
  };

  const handleClose = () => {
    onClose();
  };

  const handleSave = () => {
    const validationErrors = validate(formValues);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    onSave({
      name: formValues.name,
      dosageAmount: formValues.dosageAmount,
      dosageUnit: formValues.dosageUnit,
      purpose: formValues.purpose,
      frequencyAmount: formValues.frequencyAmount,
      frequencyInterval: formValues.frequencyInterval,
      prescribingDoctor: formValues.prescribingDoctor,
      collaborationScheduleValue: formValues.collaborationScheduleValue,
      collaborationScheduleInterval: formValues.collaborationScheduleInterval,
    });
  };

  const isEditMode = Boolean(initialValues && Object.keys(initialValues).length > 0);

  return (
    <ResponsiveModal
      open={open}
      onClose={handleClose}
      title={isEditMode ? "Edit Medication" : "Add New Medication"}
      maxWidth="xs"
      drawerContentSx={{ maxHeight: "calc(80vh - 160px)" }}
    >
      <Box sx={{ p: 0 }}>
        <Grid container spacing={7} sx={{ p: "24px 0px" }}>
          <Grid item xs={12}>
            <Stack gap={1}>
              <Typography variant="inputLabel" sx={{ color: "var(--grey-600, #798397)" }}>
                Name
              </Typography>
              <TextField
                value={formValues.name}
                onChange={handleChange("name")}
                error={!!errors.name}
                helperText={errors.name}
                inputProps={{ "aria-label": "Medication name" }}
              />
            </Stack>
          </Grid>

          <Grid item xs={12}>
            <Stack gap={1}>
              <Typography variant="inputLabel" sx={{ color: "var(--grey-600, #798397)" }}>
                Dosage
              </Typography>
              <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
                <TextField
                  type="number"
                  value={formValues.dosageAmount}
                  onChange={handleChange("dosageAmount")}
                  error={!!errors.dosageAmount}
                  inputProps={{ "aria-label": "Dosage amount" }}
                  sx={{ flex: 1 }}
                />
                <Select
                  value={formValues.dosageUnit}
                  onChange={(e) => handleSelectChange("dosageUnit")(e.target.value)}
                  sx={{ width: 110, borderRadius: "10px" }}
                >
                  {DOSAGE_UNITS.map((unit) => (
                    <MenuItem key={unit} value={unit}>
                      {unit}
                    </MenuItem>
                  ))}
                </Select>
              </Box>
              {errors.dosageAmount && <FormHelperText error>{errors.dosageAmount}</FormHelperText>}
            </Stack>
          </Grid>

          <Grid item xs={12}>
            <Stack gap={1}>
              <Typography variant="inputLabel" sx={{ color: "var(--grey-600, #798397)" }}>
                Purpose
              </Typography>
              <TextField
                value={formValues.purpose}
                onChange={handleChange("purpose")}
                multiline
                minRows={3}
                inputProps={{ "aria-label": "Medication purpose" }}
              />
            </Stack>
          </Grid>

          <Grid item xs={12}>
            <Stack gap={1}>
              <Typography variant="inputLabel" sx={{ color: "var(--grey-600, #798397)" }}>
                Frequency
              </Typography>
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                <TextField
                  type="number"
                  value={formValues.frequencyAmount}
                  onChange={handleChange("frequencyAmount")}
                  error={!!errors.frequencyAmount}
                  inputProps={{ "aria-label": "Frequency amount" }}
                  sx={{ flex: 1 }}
                />
                <Typography variant="body2" sx={{ whiteSpace: "nowrap", mt: "10px" }}>
                  per
                </Typography>
                <Select
                  value={formValues.frequencyInterval}
                  onChange={(e) => handleSelectChange("frequencyInterval")(e.target.value)}
                  sx={{ width: 130, borderRadius: "10px" }}
                >
                  {FREQUENCY_INTERVALS.map((interval) => (
                    <MenuItem key={interval} value={interval}>
                      {interval}
                    </MenuItem>
                  ))}
                </Select>
              </Box>
              {errors.frequencyAmount && (
                <FormHelperText error>{errors.frequencyAmount}</FormHelperText>
              )}
            </Stack>
          </Grid>

          <Grid item xs={12}>
            <Stack gap={1}>
              <Typography variant="inputLabel" sx={{ color: "var(--grey-600, #798397)" }}>
                Intervention Provider
              </Typography>
              <Select
                value={selectedPhysicianId}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val === "__add_name__") {
                    setIsCustomProvider(true);
                    setSelectedPhysicianId("");
                    handleSelectChange("prescribingDoctor")("");
                  } else if (val === "") {
                    setIsCustomProvider(false);
                    setSelectedPhysicianId("");
                    handleSelectChange("prescribingDoctor")("");
                  } else {
                    const physician = ((physicians as Physician[]) ?? []).find(
                      (m) => String(m.id) === val
                    );
                    setIsCustomProvider(false);
                    setSelectedPhysicianId(val);
                    handleSelectChange("prescribingDoctor")(physician?.name ?? "");
                  }
                }}
                displayEmpty
                renderValue={(selected) => {
                  if (isCustomProvider) return <span>{formValues.prescribingDoctor || ""}</span>;
                  if (!selected) return <em>None</em>;
                  const physician = ((physicians as Physician[]) ?? []).find(
                    (m) => String(m.id) === selected
                  );
                  return <span>{physician?.name ?? ""}</span>;
                }}
                sx={{ borderRadius: "10px" }}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {((physicians as Physician[]) ?? []).map((member) => {
                  const roiLabel = getRoiLabel(member);
                  return (
                    <MenuItem
                      key={member.id}
                      value={String(member.id)}
                      sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}
                    >
                      <span>{member.name}</span>
                      {roiLabel && (
                        <Typography
                          variant="body2"
                          sx={{
                            color: roiLabel === "ROI expired" ? "error.main" : "text.secondary",
                            flexShrink: 0,
                          }}
                        >
                          {roiLabel}
                        </Typography>
                      )}
                    </MenuItem>
                  );
                })}
                <MenuItem value="__add_name__">+ Add Name Only</MenuItem>
              </Select>

              <Alert severity="info" sx={{ mt: 1 }}>
                <Typography variant="body2" fontWeight={600}>
                  Release of Information Consent
                </Typography>
                <Typography variant="body2">
                  If you&apos;re actively collaborating with this provider, add them to the Care
                  Team first to send an ROI to the caregiver.
                </Typography>
              </Alert>
              <Link
                component={RouterLink}
                to={`/clients/${clientId}/care-team`}
                underline="hover"
                sx={{ display: "inline-flex", alignItems: "center", gap: 1 }}
              >
                <Typography variant="body2">Go to Care Team</Typography>
                <ArrowUpRight size={16} />
              </Link>

              {isCustomProvider && (
                <Stack gap={1}>
                  <Typography variant="inputLabel" sx={{ color: "var(--grey-600, #798397)" }}>
                    Intervention Provider Name
                  </Typography>
                  <TextField
                    value={formValues.prescribingDoctor}
                    onChange={handleChange("prescribingDoctor")}
                    autoFocus
                  />
                </Stack>
              )}
            </Stack>
          </Grid>
        </Grid>

        <Grid container spacing={7} sx={{ mt: 0 }}>
          <Grid item xs={12}>
            <Stack gap={1}>
              <Typography variant="inputLabel" sx={{ color: "var(--grey-600, #798397)" }}>
                Collaboration Schedule
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <TextField
                  type="number"
                  value={formValues.collaborationScheduleValue}
                  onChange={handleChange("collaborationScheduleValue")}
                  inputProps={{ "aria-label": "Collaboration schedule amount" }}
                  sx={{ flex: 1 }}
                />
                <Typography variant="body2" sx={{ whiteSpace: "nowrap" }}>
                  per
                </Typography>
                <Select
                  value={formValues.collaborationScheduleInterval}
                  onChange={(e) =>
                    handleSelectChange("collaborationScheduleInterval")(e.target.value)
                  }
                  sx={{ width: 130, borderRadius: "10px" }}
                >
                  {FREQUENCY_INTERVALS.map((interval) => (
                    <MenuItem key={interval} value={interval}>
                      {interval}
                    </MenuItem>
                  ))}
                </Select>
              </Box>
            </Stack>
          </Grid>
        </Grid>

        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 3, mt: "16px" }}>
          <Button variant="contained" color="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <LoadingButton variant="contained" size="small" loading={isSaving} onClick={handleSave}>
            {isEditMode ? "Save Changes" : "Add Medication"}
          </LoadingButton>
        </Box>
      </Box>
    </ResponsiveModal>
  );
};

export default MedicationsModal;
